class CustomHeader extends HTMLElement {
  constructor() {
    super();
    this.previousIsDesktop = window.innerWidth > 940;
    this.menuWrapper = null;
    this.placeholder = null;
    this.isSticky = false;
    this.stickyThreshold = 0;
    this.scrollAnimationId = null;
    this.lastScrollY = 0;
    this.scrollThrottle = 16; // ~60fps
    this.lastScrollTime = 0;
    this.languageSelectors = [];
    
    // Bottom drawer properties
    this.drawerOverlay = null;
    this.drawer = null;
    this.menuTrigger = null;
    this.drawerClose = null;
    this.isDrawerOpen = false;
  }

connectedCallback() {
    this.menuWrapper = this.querySelector('.custom-header-menu-wrapper');
    this.placeholder = this.querySelector('.custom-header-menu-wrapper-placeholder');
    
    if (this.menuWrapper) {
      this.initStickyBehavior();
      this.handleResize();
      this.lastScrollY = window.pageYOffset;
      
      // Forzar estado contraído permanente en página de Tienda
      if (window.location.pathname === '/pages/tienda') {
        this.stickyThreshold = 0;
        
        // Ocultar el header grande original
        const logoWrapper = this.querySelector('.custom-header-logo-wrapper');
        if (logoWrapper) {
          logoWrapper.style.display = 'none';
        }
        
        setTimeout(() => {
          this.makeSticky();
        }, 50);
        return;
      }
      
      window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });
      window.addEventListener('resize', this.handleResize.bind(this), { passive: true });
    }


    
    // Initialize language selector functionality
    this.initLanguageSelectors();
    
    // Initialize bottom drawer functionality
    this.initBottomDrawer();
  }

  initLanguageSelectors() {
    // Find all language selector buttons
    this.languageSelectors = this.querySelectorAll('.language-selector-button');
    
    // Add click event listeners to each button
    this.languageSelectors.forEach(button => {
      button.addEventListener('click', this.toggleLanguageDropdown.bind(this));
    });
    
    // Add click outside listener to close dropdowns
    document.addEventListener('click', this.handleClickOutside.bind(this));
  }

  toggleLanguageDropdown(event) {
    event.preventDefault();
    event.stopPropagation();
    
    const button = event.currentTarget;
    const dropdown = button.nextElementSibling;
    
    if (!dropdown || !dropdown.classList.contains('language-selector')) {
      return;
    }
    
    const isExpanded = button.getAttribute('aria-expanded') === 'true';
    
    // Close all other dropdowns first
    this.closeAllLanguageDropdowns();
    
    if (!isExpanded) {
      // Open this dropdown
      button.setAttribute('aria-expanded', 'true');
      dropdown.classList.remove('hidden');
    } else {
      // Close this dropdown
      button.setAttribute('aria-expanded', 'false');
      dropdown.classList.add('hidden');
    }
  }

  closeAllLanguageDropdowns() {
    this.languageSelectors.forEach(button => {
      const dropdown = button.nextElementSibling;
      if (dropdown && dropdown.classList.contains('language-selector')) {
        button.setAttribute('aria-expanded', 'false');
        dropdown.classList.add('hidden');
      }
    });
  }

  handleClickOutside(event) {
    // Check if click is outside any language selector
    const isOutsideLanguageSelector = !event.target.closest('.language-selector-container');
    
    if (isOutsideLanguageSelector) {
      this.closeAllLanguageDropdowns();
    }
  }

  initStickyBehavior() {
    // Calculate the threshold (when the menu wrapper reaches the top)
    this.updateStickyThreshold();
  }

  updateStickyThreshold() {
    // Calculate when the menu wrapper should become sticky
    // This is when the menu wrapper's top edge reaches the viewport top
    const menuWrapperRect = this.menuWrapper.getBoundingClientRect();
    
    // The threshold is when the menu wrapper reaches the top of the viewport
    // We need to account for the current scroll position and the menu wrapper's position
    this.stickyThreshold = window.pageYOffset + menuWrapperRect.top;
  }

  handleScroll() {
    const now = Date.now();
    
    // Throttle scroll events for better performance
    if (now - this.lastScrollTime < this.scrollThrottle) {
      return;
    }
    
    this.lastScrollTime = now;
    
    // Cancel any pending animation frame
    if (this.scrollAnimationId) {
      cancelAnimationFrame(this.scrollAnimationId);
    }
    
    // Use requestAnimationFrame for smooth performance
    this.scrollAnimationId = requestAnimationFrame(() => {
      this.processScroll();
    });
  }

  processScroll() {
    const scrollY = window.pageYOffset;
    
    // Only process if scroll position has actually changed
    if (scrollY === this.lastScrollY) {
      return;
    }
    
    this.lastScrollY = scrollY;
    
    // Use getBoundingClientRect only when necessary
    const menuWrapperRect = this.menuWrapper.getBoundingClientRect();
    
    // Check if the menu wrapper's top edge has reached the top of the viewport
    if (menuWrapperRect.top <= 0 && !this.isSticky) {
      this.makeSticky();
    } else if (scrollY < this.stickyThreshold && this.isSticky) {
      this.removeSticky();
    }
  }

  makeSticky() {
    this.isSticky = true;
    this.menuWrapper.classList.add('custom-header-menu-wrapper-sticky');
    
    if (window.innerWidth <= 749) {
      this.menuWrapper.classList.add('custom-header-menu-wrapper-sticky-mobile');
    }
    
    // Show placeholder to prevent content jump
    if (this.placeholder) {
      this.placeholder.style.display = 'block';
    }
    
    // Animate the logo wrapper
    const logoWrapper = this.querySelector('.custom-header-menu-logo-wrapper');
    if (logoWrapper) {
      // Small delay to ensure the sticky class is applied first
      requestAnimationFrame(() => {
        logoWrapper.classList.add('animate-in');
      });
    }
  }

  removeSticky() {
    this.isSticky = false;
    this.menuWrapper.classList.remove('custom-header-menu-wrapper-sticky');
    this.menuWrapper.classList.remove('custom-header-menu-wrapper-sticky-mobile');
    
    // Hide placeholder
    if (this.placeholder) {
      this.placeholder.style.display = 'none';
    }
    
    // Remove animation from logo wrapper
    const logoWrapper = this.querySelector('.custom-header-menu-logo-wrapper');
    if (logoWrapper) {
      logoWrapper.classList.remove('animate-in');
    }
  }

  handleResize() {
    const isDesktop = window.innerWidth > 940;
    
    if (isDesktop !== this.previousIsDesktop) {
      this.previousIsDesktop = isDesktop;
      
      // Reset sticky state on resize
      if (this.isSticky) {
        this.removeSticky();
      }
      
      // Update sticky threshold after resetting
      this.updateStickyThreshold();
      
      // Re-evaluate if we should be sticky at current scroll position
      const menuWrapperRect = this.menuWrapper.getBoundingClientRect();
      if (menuWrapperRect.top <= 0) {
        this.makeSticky();
      }
    }
  }

  disconnectedCallback() {
    // Clean up animation frame
    if (this.scrollAnimationId) {
      cancelAnimationFrame(this.scrollAnimationId);
    }
    
    // Remove scroll and resize listeners
    window.removeEventListener('scroll', this.handleScroll.bind(this));
    window.removeEventListener('resize', this.handleResize.bind(this));
    
    // Remove language selector listeners
    this.languageSelectors.forEach(button => {
      button.removeEventListener('click', this.toggleLanguageDropdown.bind(this));
    });
    
    // Remove document click listener
    document.removeEventListener('click', this.handleClickOutside.bind(this));
    
    // Clean up drawer functionality
    if (this.isDrawerOpen) {
      this.restoreBodyScroll();
      document.body.classList.remove('drawer-open');
    }
    
    if (this.menuTrigger) {
      this.menuTrigger.removeEventListener('click', this.toggleDrawer.bind(this));
    }
    
    if (this.drawerClose) {
      this.drawerClose.removeEventListener('click', this.closeDrawer.bind(this));
    }
    
    if (this.drawerOverlay) {
      this.drawerOverlay.removeEventListener('click', this.handleDrawerOverlayClick.bind(this));
    }
    
    // Remove cart link listener
    const cartLink = this.querySelector('.bottom-drawer-cart-link');
    if (cartLink) {
      cartLink.removeEventListener('click', this.handleCartClick.bind(this));
    }
    
    document.removeEventListener('keydown', this.handleKeydown.bind(this));
  }

  initBottomDrawer() {
    this.drawerOverlay = this.querySelector('.bottom-drawer-overlay');
    this.drawer = this.querySelector('.bottom-drawer');
    this.menuTrigger = this.querySelector('.mobile-menu-trigger');
    this.drawerClose = this.querySelector('.bottom-drawer-close');
    
    if (this.menuTrigger) {
      this.menuTrigger.addEventListener('click', this.toggleDrawer.bind(this));
    }
    
    if (this.drawerClose) {
      this.drawerClose.addEventListener('click', this.closeDrawer.bind(this));
    }
    
    if (this.drawerOverlay) {
      this.drawerOverlay.addEventListener('click', this.handleDrawerOverlayClick.bind(this));
    }
    
    // Add cart link click handler
    const cartLink = this.querySelector('.bottom-drawer-cart-link');
    if (cartLink) {
      cartLink.addEventListener('click', this.handleCartClick.bind(this));
    }
    
    // Add keyboard support
    document.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  toggleDrawer() {
    if (this.isDrawerOpen) {
      this.closeDrawer();
    } else {
      this.openDrawer();
    }
  }

  openDrawer() {
    if (this.isDrawerOpen) return;
    
    this.isDrawerOpen = true;
    this.drawerOverlay.classList.add('active');
    this.drawer.classList.add('active');
    this.menuTrigger.classList.add('drawer-open');
    this.menuTrigger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('drawer-open');
    
    // Focus management
    this.drawerClose.focus();
    
    // Prevent body scroll
    this.preventBodyScroll();
  }

  closeDrawer() {
    if (!this.isDrawerOpen) return;
    
    this.isDrawerOpen = false;
    this.drawerOverlay.classList.remove('active');
    this.drawer.classList.remove('active');
    this.menuTrigger.classList.remove('drawer-open');
    this.menuTrigger.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('drawer-open');
    
    // Focus management
    this.menuTrigger.focus();
    
    // Restore body scroll
    this.restoreBodyScroll();
  }

  handleDrawerOverlayClick(event) {
    // Close drawer if clicking on overlay (not the drawer itself)
    if (event.target === this.drawerOverlay) {
      this.closeDrawer();
    }
  }

  handleKeydown(event) {
    if (!this.isDrawerOpen) return;
    
    // Close drawer on Escape key
    if (event.key === 'Escape') {
      this.closeDrawer();
    }
  }

  preventBodyScroll() {
    // Store current scroll position
    this.scrollPosition = window.pageYOffset;
    
    // Add styles to prevent scroll
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollPosition}px`;
    document.body.style.width = '100%';
  }

  restoreBodyScroll() {
    // Remove fixed positioning
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    
    // Restore scroll position
    if (this.scrollPosition !== undefined) {
      window.scrollTo(0, this.scrollPosition);
    }
  }

  handleCartClick(event) {
    // Close the drawer first
    this.closeDrawer();
    
    // Let the default cart link behavior happen (opening cart drawer)
    // The href="#cart-drawer" will still work after the drawer closes
  }
}

customElements.define("custom-header", CustomHeader);
